/**
 * Nav chrome over hero: stock logo mark by vertical + theme surface tone.
 * Dark themes → ivory strokes; light/mid → charcoal strokes.
 *
 * Overlay / Whisper / Quiet over photography → on-dark for hero mode; panel
 * mode (no cinematic underlay) swaps to a theme-tone sibling via CSS.
 * Capsule (frosted theme glass) follows theme tone for both grounds.
 */
import {
  isThemeId,
  PLATFORM_VERTICAL_KEYS,
  THEME_ID,
  type PlatformVerticalKey,
} from '../catalog/platformCatalogIds';
import { resolveProjectColorSet } from '../theme/themeResolve';
import { classifySurfaceTone } from '../theme/themeTokens';
import {
  NAV_PRESENTATION,
  type NavPresentation,
} from './navPresentation';

export const NAV_LOGO_SURFACE = {
  onLight: 'on-light',
  onDark: 'on-dark',
} as const;

export type NavLogoSurface = (typeof NAV_LOGO_SURFACE)[keyof typeof NAV_LOGO_SURFACE];

/** File-safe mark ids used in `platform/defaults/logos/nav-mark-{id}-{surface}.svg`. */
export const NAV_LOGO_MARK = {
  restaurant: 'restaurant',
  cafe: 'cafe',
  bar: 'bar',
  foodTruck: 'food-truck',
  /** Vietnam Lotus Steam — steaming bowl (herb / broth). */
  lotusSteam: 'lotus-steam',
  /** Vietnam Tile Awning — striped awning over shopfront bowl. */
  tileAwning: 'tile-awning',
  /** Pan-Asian Lantern Court — paper lantern over bowl. */
  lanternCourt: 'lantern-court',
  /** Fine dining Michelin Dark — star over plate. */
  michelinDark: 'michelin-dark',
  /** Fine dining Limestone Orangery — leaf over plate. */
  limestoneOrangery: 'limestone-orangery',
  /** Fine dining Moorland Hearth — gable over plate. */
  moorlandHearth: 'moorland-hearth',
  /** Western bistro — wine glass over plate. */
  bistroBrasserie: 'bistro-brasserie',
  /** Sushi Hinoki Counter — freeform 檜 cypress glyph (no ring). */
  hinokiCounter: 'hinoki-counter',
  /** Stock library — freeform sushi kanji (鮨). */
  sushiKanji: 'sushi-kanji',
  /** Stock library — freeform sushi word mark (寿司). */
  sushiShushi: 'sushi-shushi',
  /** Stock library — freeform wa / harmony (和). */
  sushiWa: 'sushi-wa',
  /** Stock library — freeform umi / sea (海). */
  sushiUmi: 'sushi-umi',
  /** Stock library — freeform ki / wood (木). */
  sushiKi: 'sushi-ki',
  /** Imbiss Bold Street — folded street wrap. */
  boldStreet: 'bold-street',
  /** Imbiss Mustard Rail — sausage on paper tray. */
  mustardRail: 'mustard-rail',
  /** Imbiss Tahini Window — falafel circle + herb. */
  tahiniWindow: 'tahini-window',
} as const;

export type NavLogoMark = (typeof NAV_LOGO_MARK)[keyof typeof NAV_LOGO_MARK];

const MARK_BY_VERTICAL: Record<PlatformVerticalKey, NavLogoMark> = {
  [PLATFORM_VERTICAL_KEYS.restaurant]: NAV_LOGO_MARK.restaurant,
  [PLATFORM_VERTICAL_KEYS.cafe]: NAV_LOGO_MARK.cafe,
  [PLATFORM_VERTICAL_KEYS.bar]: NAV_LOGO_MARK.bar,
  [PLATFORM_VERTICAL_KEYS.foodTruck]: NAV_LOGO_MARK.foodTruck,
};

/** Theme-specific marks override vertical defaults (restaurant family glyphs). */
const MARK_BY_THEME: Partial<Record<string, NavLogoMark>> = {
  [THEME_ID.lotusSteam]: NAV_LOGO_MARK.lotusSteam,
  [THEME_ID.tileAwning]: NAV_LOGO_MARK.tileAwning,
  [THEME_ID.lanternCourt]: NAV_LOGO_MARK.lanternCourt,
  [THEME_ID.michelinDark]: NAV_LOGO_MARK.michelinDark,
  [THEME_ID.limestoneOrangery]: NAV_LOGO_MARK.limestoneOrangery,
  [THEME_ID.moorlandHearth]: NAV_LOGO_MARK.moorlandHearth,
  [THEME_ID.bistroBrasserie]: NAV_LOGO_MARK.bistroBrasserie,
  [THEME_ID.hinokiCounter]: NAV_LOGO_MARK.hinokiCounter,
  [THEME_ID.boldStreet]: NAV_LOGO_MARK.boldStreet,
  [THEME_ID.mustardRail]: NAV_LOGO_MARK.mustardRail,
  [THEME_ID.tahiniWindow]: NAV_LOGO_MARK.tahiniWindow,
};

export const NAV_LOGO_MARK_VALUES = Object.values(NAV_LOGO_MARK);

/** Looks that float light ink over cinematic photography (hero / menu masthead). */
export function navPresentationUsesCinematicLogo(
  presentation?: NavPresentation | null,
): boolean {
  return (
    presentation === NAV_PRESENTATION.overlay ||
    presentation === NAV_PRESENTATION.whisper ||
    presentation === NAV_PRESENTATION.quiet
  );
}

export function resolveNavLogoSurface(
  themeId?: string | null,
  presentation?: NavPresentation | null,
  colorsetId?: string | null,
): NavLogoSurface {
  // Cinematic chrome: light strokes over photography — even on paper colorsets.
  if (navPresentationUsesCinematicLogo(presentation)) {
    return NAV_LOGO_SURFACE.onDark;
  }
  if (!themeId || !isThemeId(themeId)) {
    return NAV_LOGO_SURFACE.onLight;
  }
  const resolved = resolveProjectColorSet(themeId, colorsetId ?? undefined);
  if (!resolved) {
    return NAV_LOGO_SURFACE.onLight;
  }
  return classifySurfaceTone(resolved.tokens.bgBase) === 'dark'
    ? NAV_LOGO_SURFACE.onDark
    : NAV_LOGO_SURFACE.onLight;
}

export function resolveNavLogoMark(
  vertical?: string | null,
  themeId?: string | null,
): NavLogoMark {
  if (themeId && MARK_BY_THEME[themeId]) {
    return MARK_BY_THEME[themeId]!;
  }
  if (vertical && vertical in MARK_BY_VERTICAL) {
    return MARK_BY_VERTICAL[vertical as PlatformVerticalKey];
  }
  return NAV_LOGO_MARK.restaurant;
}

export function resolveNavLogoAssetId(
  vertical?: string | null,
  themeId?: string | null,
  presentation?: NavPresentation | null,
  colorsetId?: string | null,
): { mark: NavLogoMark; surface: NavLogoSurface } {
  return {
    mark: resolveNavLogoMark(vertical, themeId),
    surface: resolveNavLogoSurface(themeId, presentation, colorsetId),
  };
}

const PLATFORM_NAV_MARK_SURFACE_RE =
  /^(.*\/nav-mark-[a-z0-9-]+-)(on-light|on-dark)(\.svg)(?:\?.*)?$/i;

/**
 * Retarget stock platform marks to the chrome surface stroke.
 * Custom uploads are left unchanged.
 */
export function retargetPlatformNavLogoPathForSurface(
  pathname: string,
  surface: NavLogoSurface,
): string {
  const match = pathname.match(PLATFORM_NAV_MARK_SURFACE_RE);
  if (!match) {
    return pathname;
  }
  const [, prefix, , ext] = match;
  return `${prefix}${surface}${ext}`;
}

/**
 * Adaptive panel twin of the cinematic on-dark mark. Panel chrome follows the
 * active colorset paper/ink (not forced on-light — ink-deep sticky bars stay dark).
 * Returns null when twin matches hero, or for custom uploads.
 */
export function resolveOverlayAdaptivePanelLogoPath(
  pathname: string | null | undefined,
  themeId?: string | null,
  colorsetId?: string | null,
): string | null {
  if (!pathname?.trim()) {
    return null;
  }
  const heroPath = retargetPlatformNavLogoPathForSurface(pathname, NAV_LOGO_SURFACE.onDark);
  const panelSurface =
    themeId && isThemeId(themeId)
      ? resolveNavLogoSurface(themeId, null, colorsetId)
      : NAV_LOGO_SURFACE.onLight;
  const panelPath = retargetPlatformNavLogoPathForSurface(pathname, panelSurface);
  if (heroPath === panelPath) {
    return null;
  }
  return panelPath;
}
